// LedgerStatement.jsx
import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Search as SearchIcon,
  Print as PrintIcon,
  ReceiptLong as StatementIcon,
} from "@mui/icons-material";

import dayjs from "dayjs";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";

// ------------------------------------------------------------
// THEME (same light blue tokens as Accounts Chart)
// ------------------------------------------------------------
const C = {
  primary: "#2b7bc9",
  primaryHover: "#2369ab",
  heading: "#24527f",
  text: "#2d3b4e",
  muted: "#6b7c93",
  page: "#f6f9fc",
  card: "#ffffff",
  soft: "#eef5fc",
  border: "#dbe7f4",
  rowLine: "#e8f0f9",
  drText: "#2369ab",
  crText: "#a85a05",
};

const W = { normal: 400, medium: 500 };

const headCell = {
  bgcolor: C.soft,
  color: C.heading,
  fontWeight: W.medium,
  fontSize: 13,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${C.border}`,
};

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------
const DATE_FMT = "DD/MM/YYYY";

// Typing 01042026 shows 01/04/2026
const maskDmy = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// dd/mm/yyyy -> yyyy-mm-dd, or null when not a real date
const dmyToIso = (value) => {
  const match = String(value || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const valid =
    date.getFullYear() === Number(yyyy) &&
    date.getMonth() === Number(mm) - 1 &&
    date.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : null;
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Positive running balance = Dr, negative = Cr
const balanceText = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) < 0.005) return "0.00";
  return `${money(Math.abs(n))} ${n > 0 ? "Dr" : "Cr"}`;
};

const balanceColor = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) < 0.005) return C.text;
  return n > 0 ? C.drText : C.crText;
};

function SummaryItem({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 160,
        px: 2,
        py: 1.25,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        bgcolor: C.card,
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: C.muted }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 17,
          fontWeight: W.medium,
          color: color || C.heading,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function LedgerStatement() {
  const { clientId } = useClientInfo();

  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [ledgerId, setLedgerId] = useState("");
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Dates are kept as dd/mm/yyyy text and converted when calling the API.
  const [fromDate, setFromDate] = useState(
    dayjs().startOf("month").format(DATE_FMT),
  );
  const [toDate, setToDate] = useState(dayjs().format(DATE_FMT));

  const fromIso = dmyToIso(fromDate);
  const toIso = dmyToIso(toDate);
  const fromError = fromDate.length === 10 && !fromIso;
  const toError = toDate.length === 10 && !toIso;

  // ---------------- LOAD GROUPS ----------------
  const loadGroups = async () => {
    try {
      if (!clientId) return;
      const res = await axiosInstance.get("/api/account-groups", {
        params: { client_id: clientId },
      });
      setGroups(res.data || []);
    } catch (err) {
      console.error("Group load error", err);
      setError("Could not load account groups.");
    }
  };

  // ---------------- LOAD LEDGERS ----------------
  const loadLedgers = async (group_id) => {
    try {
      if (!clientId) return;
      const res = await axiosInstance.get("/api/account-ledgers", {
        params: { group_id, client_id: clientId },
      });
      // A statement needs one specific ledger, so "All Ledgers" is left out.
      setLedgers((res.data || []).filter((l) => l.id !== "ALL"));
    } catch (err) {
      console.error("Ledger load error", err);
      setError("Could not load ledgers.");
    }
  };

  // ---------------- LOAD STATEMENT ----------------
  const loadLedgerStatement = async () => {
    setError("");

    if (!ledgerId) {
      setError("Please select an account group and a ledger.");
      return;
    }

    if (!fromIso || !toIso) {
      setError("Enter both dates as dd/mm/yyyy, for example 31/03/2026.");
      return;
    }

    if (fromIso > toIso) {
      setError("From Date cannot be after To Date.");
      return;
    }

    try {
      setLoading(true);

      const res = await axiosInstance.get("/api/accountant/ledger-statement", {
        params: {
          ledger_id: ledgerId,
          group_id: groupId,
          from_date: fromIso,
          to_date: toIso,
          client_id: clientId,
        },
      });

      setRows(res.data || []);
      setLoaded(true);
    } catch (err) {
      console.error("Ledger statement error", err);
      setRows([]);
      setLoaded(false);
      setError(
        err.response?.data?.message || "Could not load the ledger statement.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    loadGroups();
  }, [clientId]);

  const handleGroupChange = async (e) => {
    const value = e.target.value;
    setGroupId(value);
    setLedgerId("");
    setLedgers([]);
    setRows([]);
    setLoaded(false);
    if (value) await loadLedgers(value);
  };

  const handleLedgerChange = (e) => {
    setLedgerId(e.target.value);
    setRows([]);
    setLoaded(false);
  };

  // ---------------- TOTALS & SUMMARY ----------------
  const summary = useMemo(() => {
    let debit = 0;
    let credit = 0;
    rows.forEach((r) => {
      debit += Number(r.debit || 0);
      credit += Number(r.credit || 0);
    });

    const first = rows[0];
    const opening =
      first && first.voucher_type === "Opening"
        ? Number(first.debit || 0) - Number(first.credit || 0)
        : 0;

    const closing = rows.length
      ? Number(rows[rows.length - 1].running_balance || 0)
      : 0;

    return { debit, credit, opening, closing };
  }, [rows]);

  const selectedGroup = groups.find((g) => g.id === groupId);
  const selectedLedger = ledgers.find((l) => l.id === ledgerId);

  // ---------------- UI ----------------
  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: C.page, minHeight: "100%" }}>
      {/* Header */}
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderRadius: 2.5,
          borderColor: C.border,
          bgcolor: C.soft,
          "@media print": { display: "none" },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StatementIcon sx={{ color: C.primary, fontSize: 30 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 22, md: 26 },
                  fontWeight: W.medium,
                  color: C.heading,
                  lineHeight: 1.2,
                }}
              >
                Ledger Statement
              </Typography>
              <Typography sx={{ fontSize: 14, color: C.muted }}>
                Account-wise transactions with running balance.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={!loaded}
            onClick={() => window.print()}
            sx={{
              color: C.primary,
              borderColor: C.border,
              bgcolor: C.card,
              fontWeight: W.medium,
              textTransform: "none",
              "&:hover": { borderColor: C.primary, bgcolor: C.soft },
            }}
          >
            Print
          </Button>
        </Stack>
      </Paper>

      {/* Filters */}
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2.5,
          borderColor: C.border,
          bgcolor: C.card,
          "@media print": { display: "none" },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "1.5fr 1.5fr 1fr 1fr auto",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <TextField
            select
            label="Account Group"
            value={groupId}
            onChange={handleGroupChange}
            size="small"
            fullWidth
          >
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Ledger"
            value={ledgerId}
            onChange={handleLedgerChange}
            size="small"
            fullWidth
            disabled={!groupId}
            helperText={
              groupId && !ledgers.length ? "No ledgers in this group" : " "
            }
          >
            {ledgers.map((ledger) => (
              <MenuItem key={ledger.id} value={ledger.id}>
                {ledger.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="From Date"
            placeholder="dd/mm/yyyy"
            value={fromDate}
            onChange={(e) => setFromDate(maskDmy(e.target.value))}
            size="small"
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            error={fromError}
            helperText={fromError ? "Not a valid date" : "dd/mm/yyyy"}
          />

          <TextField
            label="To Date"
            placeholder="dd/mm/yyyy"
            value={toDate}
            onChange={(e) => setToDate(maskDmy(e.target.value))}
            size="small"
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            error={toError}
            helperText={toError ? "Not a valid date" : "dd/mm/yyyy"}
          />

          <Button
            variant="contained"
            onClick={loadLedgerStatement}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            sx={{
              height: 40,
              px: 3,
              bgcolor: C.primary,
              fontWeight: W.medium,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { bgcolor: C.primaryHover, boxShadow: "none" },
            }}
          >
            Load
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Report */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          borderColor: C.border,
          bgcolor: C.card,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: C.primary }} />
          </Box>
        ) : !loaded ? (
          <Box sx={{ py: 8, textAlign: "center", color: C.muted }}>
            <StatementIcon sx={{ fontSize: 40, color: "#b9d3ec" }} />
            <Typography sx={{ mt: 1 }}>
              Select a group and ledger, choose the dates, then press Load.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Statement heading */}
            <Box sx={{ px: 2.5, py: 2, bgcolor: C.soft }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 19,
                      fontWeight: W.medium,
                      color: C.heading,
                    }}
                  >
                    {selectedLedger?.name || "Ledger"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: C.muted }}>
                    {selectedGroup?.name}
                  </Typography>
                </Box>
                <Chip
                  label={`Period: ${fromDate} to ${toDate}`}
                  sx={{
                    bgcolor: C.card,
                    border: `1px solid ${C.border}`,
                    color: C.heading,
                    fontWeight: W.normal,
                  }}
                />
              </Stack>
            </Box>

            {/* Summary */}
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ p: 2 }}
            >
              <SummaryItem
                label="Opening balance"
                value={balanceText(summary.opening)}
                color={balanceColor(summary.opening)}
              />
              <SummaryItem
                label="Total debit"
                value={money(summary.debit)}
              />
              <SummaryItem
                label="Total credit"
                value={money(summary.credit)}
              />
              <SummaryItem
                label="Closing balance"
                value={balanceText(summary.closing)}
                color={balanceColor(summary.closing)}
              />
            </Stack>

            {/* Table */}
            <TableContainer sx={{ maxHeight: { md: "60vh" } }}>
              <Table
                stickyHeader
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    px: 1.5,
                    py: 1,
                    fontSize: 13.5,
                    fontWeight: W.normal,
                    borderColor: C.rowLine,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCell, width: 110 }}>Date</TableCell>
                    <TableCell sx={headCell}>Particulars</TableCell>
                    <TableCell sx={{ ...headCell, width: 110 }}>
                      Vch Type
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: 140 }}>
                      Vch No
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: 130 }}>
                      Debit
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: 130 }}>
                      Credit
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: 150 }}>
                      Balance
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => {
                    const isOpening = row.voucher_type === "Opening";
                    return (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          bgcolor: isOpening ? "#f7fafd" : "transparent",
                          "&:hover": {
                            bgcolor: `${C.soft} !important`,
                          },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.entry_date
                            ? dayjs(row.entry_date).format(DATE_FMT)
                            : ""}
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: W.medium,
                              fontStyle: isOpening ? "italic" : "normal",
                            }}
                          >
                            {row.particulars}
                          </Typography>
                          {row.narration && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: C.muted,
                                mt: 0.25,
                              }}
                            >
                              Narration: {row.narration}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>{row.voucher_type}</TableCell>
                        <TableCell>{row.voucher_no}</TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {Number(row.debit || 0) ? money(row.debit) : ""}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {Number(row.credit || 0) ? money(row.credit) : ""}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: W.medium,
                            color: balanceColor(row.running_balance),
                          }}
                        >
                          {balanceText(row.running_balance)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!rows.length && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 6, color: C.muted }}
                      >
                        No transactions found for this ledger in the selected
                        period.
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Total */}
                  {!!rows.length && (
                    <TableRow
                      sx={{
                        "& td": {
                          bgcolor: C.soft,
                          color: C.heading,
                          fontWeight: W.medium,
                          borderTop: `1px solid ${C.border}`,
                          borderBottom: "none",
                          fontVariantNumeric: "tabular-nums",
                        },
                      }}
                    >
                      <TableCell colSpan={4}>Total</TableCell>
                      <TableCell align="right">{money(summary.debit)}</TableCell>
                      <TableCell align="right">
                        {money(summary.credit)}
                      </TableCell>
                      <TableCell align="right">
                        {balanceText(summary.closing)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
}
